import { ApiError } from "@/services/http";
import type {
	JiraApprovedProject,
	JiraConnection,
	JiraProjects,
	JiraSiteProject,
} from "@/types/api";

/** Accept a Jira Cloud hostname or HTTPS origin; the backend verifies the site. */
export function parseJiraSiteUrl(value: string): string {
	const input = value.trim();
	const message =
		"Enter a Jira Cloud site URL like https://your-team.atlassian.net.";
	if (!input || input.length > 255) throw new Error(message);
	try {
		const url = new URL(
			/^https?:\/\//i.test(input) ? input : `https://${input}`,
		);
		if (
			url.protocol !== "https:" ||
			!url.hostname.includes(".") ||
			url.hostname === "localhost" ||
			/^\d+(?:\.\d+){3}$/.test(url.hostname) ||
			url.username ||
			url.password ||
			url.port ||
			url.pathname !== "/" ||
			url.search ||
			url.hash
		)
			throw new Error(message);
		return url.origin;
	} catch {
		throw new Error(message);
	}
}

/**
 * Where a connected site is in installing the Curo app, which posts the comments and is installed once
 * by a Jira site admin. "not_applicable" covers a site that is not connected and a backend that posts
 * comments as a person (or is too old to say).
 */
export type ForgeStepState =
	| "not_applicable"
	| "ready"
	| "install"
	| "install_unlinked";

/** The install link is shown to customers, so only an https link on atlassian.com is trusted. */
export function isForgeInstallUrl(value: string | undefined): value is string {
	if (!value) return false;
	try {
		const url = new URL(value);
		return (
			url.protocol === "https:" &&
			(url.hostname === "atlassian.com" ||
				url.hostname.endsWith(".atlassian.com"))
		);
	} catch {
		return false;
	}
}

export function forgeStepState(
	status: JiraConnection | undefined,
): ForgeStepState {
	if (!status?.connected || status.commentIdentity !== "forge")
		return "not_applicable";
	if (status.forgeReady) return "ready";
	return isForgeInstallUrl(status.forgeInstallUrl)
		? "install"
		: "install_unlinked";
}

/** How often to re-check while the customer is installing the app, or false when there is nothing to wait for. */
export const FORGE_INSTALL_POLL_MS = 4000;

export function forgeInstallRefetchInterval(
	status: JiraConnection | undefined,
): number | false {
	return forgeStepState(status) === "install" ? FORGE_INSTALL_POLL_MS : false;
}

/** One site project as the approval list shows it; `approval` is set when it is approved now. */
export interface ApprovalRow {
	id: string;
	key: string;
	name: string;
	approval: JiraApprovedProject | null;
}

/** The site's projects in name order, each with its approval if it has one. */
export function approvalRows(data: JiraProjects): ApprovalRow[] {
	const approved = new Map(data.approved.map((a) => [a.id, a]));
	return [...data.projects]
		.sort((a, b) => a.name.localeCompare(b.name))
		.map((p) => ({ ...p, approval: approved.get(p.id) ?? null }));
}

/**
 * Approved projects that are no longer on the site. The Gateway refuses a list naming them, so saving
 * leaves them out, which removes their approval.
 */
export function goneProjects(data: JiraProjects): JiraApprovedProject[] {
	const onSite = new Set(data.projects.map((p) => p.id));
	return data.approved.filter((a) => !onSite.has(a.id));
}

/** The whole list to save: the ticked projects that are on the site, in site order. */
export function approvedListToSave(
	data: JiraProjects,
	ticked: ReadonlySet<string>,
): JiraSiteProject[] {
	return data.projects.filter((p) => ticked.has(p.id));
}

/** Ticked projects that are not approved yet; saving these needs the admin's confirmation. */
export function newlyApproved(
	data: JiraProjects,
	ticked: ReadonlySet<string>,
): JiraSiteProject[] {
	const approved = new Set(data.approved.map((a) => a.id));
	return approvedListToSave(data, ticked).filter((p) => !approved.has(p.id));
}

/** Whether saving `ticked` would change the approved list. */
export function approvalsChanged(
	data: JiraProjects,
	ticked: ReadonlySet<string>,
): boolean {
	const saved = new Set(data.approved.map((a) => a.id));
	const next = approvedListToSave(data, ticked);
	return next.length !== saved.size || next.some((p) => !saved.has(p.id));
}

/** The ids approved now; empty before the list has loaded. */
export function approvedIds(data: JiraProjects | undefined): Set<string> {
	return new Set(data?.approved.map((a) => a.id));
}

/**
 * What pressing Save does: nothing when the list would not change, save it straight away when it only keeps or
 * removes approvals, and ask first when it approves a project that is not approved yet.
 */
export type SavePlan =
	| { kind: "none" }
	| { kind: "save"; list: JiraSiteProject[] }
	| { kind: "confirm"; list: JiraSiteProject[]; fresh: JiraSiteProject[] };

export function savePlan(
	data: JiraProjects,
	ticked: ReadonlySet<string>,
): SavePlan {
	if (!approvalsChanged(data, ticked)) return { kind: "none" };
	const list = approvedListToSave(data, ticked);
	const fresh = newlyApproved(data, ticked);
	return fresh.length > 0
		? { kind: "confirm", list, fresh }
		: { kind: "save", list };
}

/** What to tell the admin when loading or saving the approved projects fails. */
export function approvedProjectsErrorMessage(error: unknown): string {
	if (error instanceof ApiError) {
		if (error.status === 404)
			return "Jira is no longer connected. Reconnect it to approve projects.";
		if (error.code === "jira_integration_unavailable")
			return "Jira projects are unavailable right now. Please try again shortly.";
		if (error.code === "unknown_project")
			return "Some projects are no longer on your Jira site. The list has been refreshed; review it and save again.";
		if (error.status === 400)
			return "Curo could not save that list. Reload the page and try again.";
	}
	return "Could not reach Curo. Please try again.";
}
